import React, { useCallback, useMemo } from 'react'
import { format, isValid } from 'date-fns'
import { Typography } from '@mui/material'
import { CustomRangeObject, DateRangePickerV2 } from '@labrav/react-components'
import { useNavigate, useParams } from 'react-router-dom'
import {
  actionTypeWrapper,
  CloudType,
  LoadingTypes,
  setSelectedAccount,
  startLoading,
  stopLoading,
  useAccountId,
  useCloudAccounts,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  useCurrentCloudProvider,
  useScreenSize,
} from '@labrav/common'

import { useFlagValue } from '@labrav/flags'

import { ProductSelectorDropdown } from '../../../common/components/ProductSelectorDropdown/ProductSelectorDropdown'
import { useStyles } from './RevenueInsightsFilters.style'
import { getRangeDates, stripTime } from '../../../common/utils/getDateRanges'
import { customDateRanges } from '../../../common/utils/revenueConstants'
import { useDispatch, useSelector } from '../../../store/hooks'
import {
  DateRange,
  InsertDates,
} from '../../../modules/revenueInsightsV2/types'
import {
  getRevenueInsights,
  updateDateRange,
} from '../../../modules/revenueInsightsV2/action'
import {
  convertToFullDateTime,
  formatDateToISO,
} from '../../../common/utils/revenueHelpers'
import { CloudAccountChangeParams, CloudAccountChangeV2 } from '@labrav/common'
import {
  clearProductsListing,
  setSelectedProductOnListing,
} from '../../../modules/productsListing/action'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import { FlyoutFunction } from '../../../modules/productsListing/types'

export interface Range {
  startDate: Date
  endDate: Date
}

export const RevenueInsightsFilters: React.FC = () => {
  const { classes } = useStyles()
  const today = new Date()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { value: isGcpRevenueEnable } = useFlagValue('gcpRevenue')
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const dateRange = useSelector<DateRange>(
    state => state.revenueInsightsV2.dateRange
  )
  const insertDates = useSelector<InsertDates | undefined>(
    state => state.revenueInsightsV2.insertDates
  )
  const { pageSize: pageSizeValue } = useScreenSize()

  const marketplaceCloudAccounts = useCloudAccounts('flyout')

  const filteredCloudAccounts = useMemo(
    () =>
      marketplaceCloudAccounts.map(account => ({
        ...account,
        disabled: account.provider === 'GCP' && !isGcpRevenueEnable,
      })),
    [marketplaceCloudAccounts, isGcpRevenueEnable]
  )

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )

  const { startDate, endDate } = dateRange
  const formattedStartDate = useMemo(() => {
    return convertToFullDateTime(startDate) as unknown as Date
  }, [startDate])

  const formattedEndDate = useMemo(() => {
    return convertToFullDateTime(endDate) as unknown as Date
  }, [endDate])

  const formatRangeLabel = (label: string, startDate: Date, endDate: Date) => (
    <div className={classes.labelContent}>
      <span className={classes.labelContentTitle}>{label}</span>
      <Typography className={classes.labelSubTitle}>
        {isValid(startDate) ? format(startDate, 'MMM dd') : 'Invalid date'} -
        {isValid(endDate) ? format(endDate, 'MMM dd') : 'Invalid date'}
      </Typography>
    </div>
  )

  const customRanges: CustomRangeObject[] = customDateRanges.map(label => {
    const { startDate, endDate } = getRangeDates(
      label,
      today,
      insertDates?.invoiceDate
    )
    return {
      label: formatRangeLabel(label, startDate, endDate),
      range: () => ({ startDate, endDate }),
      isSelected(range: Range) {
        const { startDate, endDate } = getRangeDates(
          label,
          today,
          insertDates?.invoiceDate
        )
        return (
          stripTime(range.startDate).getTime() ===
            stripTime(startDate).getTime() &&
          stripTime(range.endDate).getTime() === stripTime(endDate).getTime()
        )
      },
    }
  })

  const handleDateRangeChange = async (range: Range) => {
    const startDateIso = formatDateToISO(range.startDate)
    const endDateIso = formatDateToISO(range.endDate)
    await dispatch(
      updateDateRange({ startDate: startDateIso, endDate: endDateIso })
    )
    await dispatch(
      getRevenueInsights({
        downloadReport: false,
        partnerId: accountId,
        pageSizeValue,
        cloudMarketplace: selectedCloudTypeProductListing,
      })
    )
  }

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const nextAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params

      const cloudPath = cloud.toLowerCase()
      navigate(`/flyout/${cloudPath}/${nextAccountId}/revenue`, {
        replace: true,
      })

      await dispatch(startLoading(LoadingTypes.GENERAL))

      await dispatch(
        setSelectedAccount(nextAccountId, 'flyout', cloud as CloudType)
      )

      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )

      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))

      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [dispatch, navigate, selectedCloudTypeProductListing]
  )

  return (
    <div className={classes.filtersWrapper}>
      <div
        className={classes.dropdownAndProduct}
        data-testid="cloud-product-selector"
      >
        <CloudAccountChangeV2
          cloudAccounts={filteredCloudAccounts}
          selectedAccountId={selectedAccountIdWithCloud}
          onChange={handleCloudAccountChange}
          width={220}
          size="small"
        />
        <div className={classes.dropDownWidth}>
          <ProductSelectorDropdown functionType={FlyoutFunction.REVENUE} />
        </div>
      </div>

      <div className={classes.dateRangePickerWrrapper}>
        <DateRangePickerV2
          keyName={'date-rance-calander'}
          title={'Invoice date range'}
          onDateRangeChange={handleDateRangeChange}
          customRanges={customRanges}
          initialRange={{
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            key: 'selection',
          }}
        />
      </div>
    </div>
  )
}
